import { computed, effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { GcMenuComponent } from '../components/gc-menu/gc-menu.component';
import { SpsMenuItem } from '../interfaces/sps-menu-item';
import { I18nService } from '../../core/services/i18n.service';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {

  /**
   * Sidebar-Menu-Reference
   * @private
   */
  sidebarMenu: GcMenuComponent;

  /**
   * signal for blocking the complete document
   */
  readonly blockDocument: WritableSignal<boolean> = signal(false);

  /**
   * Contend offsets to calculate the available content space
   * for maxed and default workspace size
   * usableContentHeight is needed for autosize components to
   * the maxsize of the available content and to calculate
   * the scrollX height i.e. for tables
   */
  readonly contentOffsetDefault: number = 180;
  readonly contentOffsetMaxedWs: number = 60;

  /**
   * signal and list for themes
   */
  readonly activeTheme: WritableSignal<string> = signal('spspurple');
  availableThemes: SelectItem[] = [];
  selectedTheme: string = 'spspurple';

  /**
   * signal for mobile detection
   */
  readonly isMobile: WritableSignal<boolean> = signal(false);
  /**
   * signal for expanded / collapsed topbar menu item
   */
  readonly  menuExpanded: WritableSignal<boolean> = signal(true);
  /**
   * signal for workspace size changes
   */
  readonly maxWorkspace: WritableSignal<boolean> = signal(false);
  private isMaxedWorkspace: boolean = false;
  /**
   * signal for content height change detection
   */
  readonly contentHeight: WritableSignal<number> = signal(window.innerHeight);

  readonly usableContentHeight: Signal<number> = computed(() => {
    if (this.maxWorkspace()) {
      return this.contentHeight() - this.contentOffsetMaxedWs;
    } else {
      return this.contentHeight() - this.contentOffsetDefault;
    }
  })

  /**
   * Home breadcrumb item is always visible
   */
  homeBreadcrumbItem: SpsMenuItem | undefined;

  /**
   * global progress dialog
   */
  showProgressDialog: boolean = false;
  progressDialogTitle: string = '';
  showProgressSpinner: boolean = false;
  readonly progressValue: WritableSignal<number> = signal(0);

  /**
   * Flag to refresh the content area of the layout
   */
  refreshContentFlag: boolean = false;

  constructor(private i18n: I18nService) {

    this.i18n.breadcrumbMenuItems.set([]);
    this.homeBreadcrumbItem = {
      icon: 'pi pi-home',
      routerLink: '/dashboard',
    };
    this.availableThemes = [
      {
        label: 'SPS-Green',
        value: 'spsgreen'
      },
      {
        label: 'SPS-Purple',
        value: 'spspurple'
      },
    ]
    effect(() => {
      if (this.isMaxedWorkspace !== this.maxWorkspace()) {
        this.isMaxedWorkspace = this.maxWorkspace();
        this.contentHeight.set(window.innerHeight);
      }
    });
  }

  /**
   * if refreshing of content area of the layout is needed
   * for example if a data-table is shown and the language was changed
   */
  refreshContent() {
    this.refreshContentFlag = true;
    setTimeout(() => {
      this.refreshContentFlag = false;
    }, 10);
  }
}
